import CustomButton from "Components/Custom/CustomButton";
import CustomInput from "Components/Custom/CustomInput";
import { useAppSelector } from "Components/Hooks/hooks";
import { GlobalImage } from "config/image";
import { FormikProps } from "formik";
import { BusinessInfoValues } from "pages/Settings/Cards/Profile";
import React, { useCallback, useEffect, useState } from "react";
import { SketchPicker } from "react-color";
import { Col, Row, Spinner } from "reactstrap";
import { compressImage } from "utils/compressImage";
import Select from "react-select";
import { useDebounce } from "Components/Hooks/useDebounce";
import { getAddressApi } from "helpers/ApiManager";
import { ErrorToast } from "utils/toast";

interface BusinessInfoProps {
  validation: FormikProps<BusinessInfoValues>;
  selectedFile: File | null;
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;
  color: string;
  setColor: React.Dispatch<React.SetStateAction<string>>;
  isOn: boolean;
  setIsOn: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading?: boolean;
  isShowCompanyName?: boolean;
  handleToggleShowCompanyName?: () => void;
}

interface AddressType {
  postcode: string;
  latitude: number;
  longitude: number;
  formatted_address: string;
  address_line_1: string;
  address_line_2: string;
  address_line_3: string;
  address_line_4: string;
  city: string;
  country: string;
  district: string;
  county: string;
  manually: boolean;
}

interface AddressOption {
  label: string;
  value: string;
  raw: AddressType;
}

const BusinessInfo = ({
  validation,
  selectedFile,
  setSelectedFile,
  color,
  setColor,
  isOn,
  setIsOn,
  isLoading,
  isShowCompanyName,
  handleToggleShowCompanyName,
}: BusinessInfoProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const { user } = useAppSelector((state) => state?.auth);
  const [selectedImage, setSelectedImage] = useState<string>(
    user?.company?.logo || GlobalImage?.businessImagePlaceholder,
  );

  const [searchPostcode, setSearchPostcode] = useState<string>("");
  const debounceSearchPostcode = useDebounce(searchPostcode, 500);
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSearch = useCallback((value: string) => {
    setSearchPostcode(value);
  }, []);

  const handleClear = useCallback(() => {
    setAddressOptions([]);
  }, []);

  useEffect(() => {
    const fetchPostcodeData = async () => {
      try {
        setLoading(true);
        const res = await getAddressApi(debounceSearchPostcode);
        if (res) {
          console.log("Address Data:", JSON.stringify(res?.payload));
          const options: AddressOption[] = res?.payload?.map(
            (ads: AddressType) => ({
              label: ads?.formatted_address,
              value: `${ads?.postcode}-${ads?.formatted_address}`,
              raw: ads,
            }),
          );
          console.log("Address Options:", options);

          setAddressOptions(() => options);
        }
      } catch (error) {
        console.error("Error fetching address data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (debounceSearchPostcode && debounceSearchPostcode.length > 3) {
      fetchPostcodeData();
    }
  }, [debounceSearchPostcode]);

  const handleImageUpload = async (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type - only allow JPEG and PNG
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const allowedExtensions = ["jpg", "jpeg", "png"];

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension || "")
    ) {
      e.target.value = ""; // Reset the input
      ErrorToast("Only JPEG and PNG images are allowed");
      return;
    }

    const compressed = await compressImage(file);
    setSelectedFile(compressed);
    const previewUrl = URL.createObjectURL(compressed);
    setSelectedImage(previewUrl);
  };

  const handleColorPicker = () => {
    setOpen((prev) => !prev);
  };

  const handleColorChange = (newColor: any) => {
    setColor(newColor.hex);
    // setOpen(false);
  };

  const handleToggle = () => {
    setIsOn((prev) => !prev);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove all non-digit characters
    const cleanValue = value.replace(/[^\d]/g, "");

    // Build the formatted value
    let formattedValue = "";
    if (cleanValue.length > 0) {
      // Add country code
      const countryCode = cleanValue.startsWith("44")
        ? cleanValue.substring(0, 2)
        : "44";
      const phoneNumber = cleanValue.startsWith("44")
        ? cleanValue.substring(2)
        : cleanValue;

      // Format as (+44) XXXXXXXXXX for display (with space)
      formattedValue = `(+${countryCode}) ${phoneNumber}`;
    }

    // Update formik with formatted value for display
    validation.setFieldValue("phone", formattedValue);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
      }}
      action="#"
    >
      <Row>
        {/* IMAGE */}
        <Col md="12" className="mb-1 mt-3">
          <div className="d-flex flex-wrap align-items-start gap-4">
            <div className="position-relative">
              <div className="profile-img-wrapper position-relative d-inline-block">
                <div
                  style={{
                    width: 100,
                    height: 100,
                  }}
                  className="profile-img rounded-circle overflow-hidden"
                >
                  <img
                    src={selectedImage}
                    alt="Profile"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.currentTarget.src =
                        GlobalImage.businessImagePlaceholder;
                    }}
                  />
                </div>
                <label className="edit-profile-btn position-absolute bg-primary btn-press-effect">
                  <i
                    className="ri-pencil-fill text-white"
                    style={{ fontSize: 18, marginTop: 2 }}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
            </div>

            <Col xs="12" sm="6" lg="4" className="mt-3">
              <div className="d-flex flex-column ">
                <p className="font-primary fs-14 text-black2D fw-norma">
                  Select Brand Colour
                </p>

                <div
                  className="brand-color-container"
                  onClick={handleColorPicker}
                >
                  <div
                    className="brand-color-picker"
                    style={{ backgroundColor: color }}
                  />
                </div>

                {open && (
                  <div
                    className="color-picker-overlay"
                    onMouseDown={(e) => {
                      if (e.target === e.currentTarget) {
                        handleColorPicker();
                      }
                    }}
                  >
                    <div onClick={(e) => e.stopPropagation()}>
                      <SketchPicker
                        color={color}
                        onChange={handleColorChange}
                      />
                    </div>
                  </div>
                )}
              </div>
            </Col>
          </div>
        </Col>
        <p className="font-primary fs-14 text-black2D fw-normal mt-0">
          Your logo will appear on quotes, invoices, and client emails.
        </p>
        <Col md="12">
          <div className="divider" />
        </Col>
        {/* User Details */}
        <Col md="12">
          <Row>
            <Col md="6" className="mb-1">
              <CustomInput
                label="Business Name"
                placeholder="Enter your business name"
                type="text"
                name="businessName"
                value={validation.values.businessName}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                touched={validation.touched.businessName}
                error={validation.errors.businessName}
              />
            </Col>

            <Col md="6" className="mb-1">
              <CustomInput
                label="Business Phone Number"
                placeholder="Enter your phone number"
                name="phone"
                type="text"
                value={validation.values.phone}
                onChange={handlePhoneChange}
                onBlur={validation.handleBlur}
                touched={validation.touched.phone}
                error={validation.errors.phone}
              />
            </Col>

            <Col>
              <div className="d-flex gap-2 align-content-center mt-1">
                <p className="font-primary fs-14 text-black2D fw-normal">
                  Show business name on Quotes & Invoices
                </p>
                <div
                  className={`toggle-switch ${
                    isShowCompanyName ? "on" : "off"
                  }`}
                  onClick={handleToggleShowCompanyName}
                >
                  <span
                    className={`toggle-label ${
                      isShowCompanyName ? "on" : "off"
                    }`}
                  >
                    {isShowCompanyName ? "Yes" : "No"}
                  </span>
                  <div
                    className={`toggle-circle ${
                      isShowCompanyName ? "on" : "off"
                    }`}
                  />
                </div>
              </div>
            </Col>

            <Col md="12">
              <div className="divider" />
            </Col>

            <Col md="12" className="mb-3">
              <div className="flex-fill field-inline">
                <Select
                  value={searchPostcode}
                  inputValue={searchPostcode}
                  filterOption={() => true}
                  onChange={(
                    selectedOption: AddressOption,
                    actionMeta: any,
                  ) => {
                    if (actionMeta.action === "clear") {
                      handleClear();
                      return;
                    }

                    if (!selectedOption) {
                      return;
                    }

                    const rawData = selectedOption?.raw;

                    const fullStreetAddress = [
                      rawData.address_line_1,
                      rawData.address_line_2,
                      rawData.address_line_3,
                      rawData.address_line_4,
                    ]
                      .map((line) => line?.trim())
                      .filter(Boolean)
                      .join(", ");

                    validation.resetForm();

                    validation.setFieldValue(
                      "streetAddress",
                      fullStreetAddress,
                    );

                    validation.setFieldValue("city", rawData.city);
                    validation.setFieldValue("zipCode", rawData.postcode);
                    validation.setFieldValue("country", rawData.country);

                    setSearchPostcode("");
                    setAddressOptions([]);
                  }}
                  onInputChange={(inputValue: any) => {
                    handleSearch(inputValue);
                  }}
                  options={addressOptions}
                  placeholder="Search postcode"
                  classNamePrefix="react-select"
                  className={`auth-form-dropdown-control`}
                  isLoading={loading}
                  loadingMessage={() => <Spinner size="sm" color="primary" />}
                  noOptionsMessage={({ inputValue }: { inputValue: any }) =>
                    inputValue
                      ? `No postcode found for "${inputValue}"`
                      : "No postcode available"
                  }
                  isClearable
                  styles={{
                    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    control: (base: any) => ({
                      ...base,
                      borderColor: base.borderColor,
                      backgroundColor: "#fff",
                      "&:hover": {
                        borderColor: base.borderColor,
                      },
                    }),
                  }}
                />
              </div>
            </Col>

            <Col md="12" className="mb-1">
              <CustomInput
                label="Street Address"
                placeholder="Enter address"
                type="text"
                name="streetAddress"
                value={validation.values.streetAddress}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                touched={validation.touched.streetAddress}
                error={validation.errors.streetAddress}
              />
            </Col>

            <Col md="12">
              <Row>
                <Col md="6" className="mb-1">
                  <CustomInput
                    label="City"
                    placeholder="Enter city"
                    name="city"
                    type="text"
                    value={validation.values.city}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    touched={validation.touched.city}
                    error={validation.errors.city}
                  />
                </Col>
                <Col md="6" className="mb-1">
                  <CustomInput
                    label="Postcode"
                    placeholder="Enter postcode"
                    name="zipCode"
                    type="text"
                    value={validation.values.zipCode}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    touched={validation.touched.zipCode}
                    error={validation.errors.zipCode}
                  />
                </Col>
              </Row>
            </Col>

            <Col md="12" className="mb-1">
              <CustomInput
                label="Country"
                placeholder="Enter country"
                type="text"
                name="country"
                value={validation.values.country}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                touched={validation.touched.country}
                error={validation.errors.country}
              />
            </Col>
            <Col md="12" className="mb-1">
              <div className="divider" />
            </Col>

            <div className="d-flex gap-2 align-content-center">
              <p className="font-primary fs-14 text-black2D fw-normal">
                Are you VAT registered?
              </p>
              <div
                className={`toggle-switch ${isOn ? "on" : "off"}`}
                onClick={handleToggle}
              >
                <span className={`toggle-label ${isOn ? "on" : "off"}`}>
                  {isOn ? "Yes" : "No"}
                </span>
                <div className={`toggle-circle ${isOn ? "on" : "off"}`} />
              </div>
            </div>

            {isOn && (
              <Col md="12" className="mb-1">
                <CustomInput
                  label="VAT Number"
                  placeholder="Enter vat number"
                  type="text"
                  name="vatNumber"
                  value={validation.values.vatNumber}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  touched={validation.touched.vatNumber}
                  error={validation.errors.vatNumber}
                />
              </Col>
            )}

            <Col md="12" className="mt-2">
              <CustomButton
                text="Update Profile"
                type="submit"
                className="bg-primary br-7"
                loading={isLoading}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </form>
  );
};

export default BusinessInfo;
